import React, { useEffect } from "react";
import { Box, Stack, Link, Button, Alert, AlertTitle } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";

import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";

import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { cleanFormData } from "../helpers/formHelpers";
import {
  loginUser,
  resendVerificationEmail,
  resetAuthServerStatus,
  clearUserInfoError,
} from "../redux/actions/authActions";
import { UserLoginDto } from "../shared/dtos/auth/UserLoginDto";

const userLoginValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .min(4, "Password must be at least 4 characters")
    .required("Password is required"),
});

const LoginForm: React.FC = () => {
  const dispatch = useAppDispatch();

  const { loadingUserInfo, errorLoadingUserInfo, serverStatus } =
    useAppSelector((state) => state.auth);

  const initialValues: UserLoginDto = {
    email: "",
    password: "",
  };

  const isPendingVerification =
    errorLoadingUserInfo === "User account pending verification.";

  useEffect(() => {
    if (!serverStatus) return;

    const { operation, status } = serverStatus;

    if (operation === "resend-verification" && status === 200) {
      dispatch(clearUserInfoError());
      dispatch(resetAuthServerStatus());
    }
  }, [serverStatus, dispatch]);

  return (
    <Box sx={{ width: "100%" }}>
      <Formik
        initialValues={initialValues}
        validationSchema={userLoginValidationSchema}
        onSubmit={(values) => {
          const cleaned = cleanFormData(values) as UserLoginDto;
          dispatch(loginUser(cleaned));
        }}
      >
        {({ values }: FormikProps<UserLoginDto>) => (
          <Form>
            <Stack spacing={2}>
              {errorLoadingUserInfo && (
                <Alert
                  severity={isPendingVerification ? "warning" : "error"}
                  sx={{ mb: 2, textAlign: "left" }}
                  action={
                    isPendingVerification && (
                      <Button
                        color="inherit"
                        disabled={!values.email}
                        size="small"
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() =>
                          dispatch(
                            resendVerificationEmail({
                              email: values.email,
                            }),
                          )
                        }
                      >
                        Resend
                      </Button>
                    )
                  }
                >
                  <AlertTitle>
                    {isPendingVerification
                      ? "Account Not Active"
                      : "Login Failed"}
                  </AlertTitle>
                  {errorLoadingUserInfo}
                </Alert>
              )}

              <FormTextField
                name="email"
                label="Email Address"
                placeholder="admin@school.com"
                type="email"
                slotProps={{
                  input: {
                    startAdornment: (
                      <EmailOutlinedIcon
                        sx={{ color: "action.active", mr: 1 }}
                      />
                    ),
                  },
                }}
              />

              <Box>
                <FormTextField
                  name="password"
                  label="Password"
                  placeholder="Enter password"
                  type="password"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <LockOutlinedIcon
                          sx={{ color: "action.active", mr: 1 }}
                        />
                      ),
                    },
                  }}
                />

                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}
                >
                  <Link
                    component={RouterLink}
                    to="/forgot-password"
                    variant="caption"
                    underline="hover"
                    sx={{
                      color: "secondary.main",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                    }}
                  >
                    Forgot password?
                  </Link>
                </Box>
              </Box>

              <SubmitButton
                loading={loadingUserInfo}
                title="Login"
                loadingText="Authenticating..."
                endIcon={<LoginOutlinedIcon />}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default LoginForm;
